import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TriangleAlert, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getEffectivePlanPresentation, getAccessEndingSoon } from '../constants/access';

// Warns an account whose paid access is genuinely about to lapse --
// deliberately NOT a freely-renewing Stripe subscription (that's not
// "ending", it just bills again -- see getAccessEndingSoon), and applies
// equally to a cancelled subscription, a redeemed access code, and an
// admin/one-time grant, since all three are just as capable of quietly
// running out unnoticed. accessState (from useAuth, already loaded
// app-wide) carries both the live subscription and entitlement data
// _evaluate_access_for_user resolves server-side, so this needs no
// dedicated fetch of its own -- unlike AnnouncementBanner, which owns its
// own /announcements poll for admin-authored notices.
//
// Dismissal is per browser session (sessionStorage), not permanent --
// this is a real billing warning, so a single click shouldn't silence it
// forever; it comes back next visit as a reminder. It's keyed on the
// specific end date rather than just "dismissed: yes/no", so dismissing
// today's warning doesn't also suppress a later, different ending event
// (e.g. a fresh cancellation after re-subscribing).
const dismissedSessionKey = (userId, endsAtMs) => `cbat_access_ending_dismissed:${userId}:${endsAtMs}`;

const readDismissed = (key) => {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch (error) {
    return false;
  }
};

const AccessEndingBanner = () => {
  const location = useLocation();
  const { user, profile, accessState, siteSettings, isAdmin } = useAuth();
  const [dismissedKey, setDismissedKey] = useState(null);

  if (!user || location.pathname.startsWith('/module/')) return null;

  const activeFamily = profile?.preparation_preferences?.active_family || 'military';
  const effectivePlan = getEffectivePlanPresentation(accessState, accessState, isAdmin, siteSettings, activeFamily);
  const ending = getAccessEndingSoon(effectivePlan);
  if (!ending) return null;

  const { daysUntil, endsAtMs } = ending;
  const key = dismissedSessionKey(user.id, endsAtMs);
  if (dismissedKey === key || readDismissed(key)) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(key, '1');
    } catch (error) {
      // Storage can throw in a private window or with site data blocked --
      // the in-memory dismissedKey below still hides it for this render.
    }
    setDismissedKey(key);
  };

  const dayLabel = daysUntil <= 0 ? 'today' : daysUntil === 1 ? 'in 1 day' : `in ${daysUntil} days`;
  const renewLabel = effectivePlan.source === 'stripe' ? 'Restart your subscription' : 'Renew your access';

  return (
    <section
      className="access-ending-banner border-b border-[#6f5b32] bg-[#241c0d]/95"
      aria-label="Access ending soon"
      aria-live="polite"
      data-testid="access-ending-banner"
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-3 px-4 py-3 text-white sm:flex-row sm:items-center sm:px-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-current bg-black/20 text-[#f4c86d]">
            <TriangleAlert size={17} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#f4c86d]">
              Access ending
            </span>
            <p className="mt-1 text-sm leading-relaxed text-[#edf5ff]">
              Your access ends {dayLabel}. {renewLabel} to keep full access.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          <Link
            to="/subscription"
            className="site-button site-button--primary !min-h-8 shrink-0 !py-1.5"
          >
            {renewLabel}
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            title="Dismiss for this session"
            aria-label="Dismiss access ending warning"
            className="site-icon-button h-8 w-8 shrink-0"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default AccessEndingBanner;
